import { useCallback, useEffect, useState } from 'react'
import { api } from './api.ts'
import { STAGE_LABEL, STATUS_LABEL } from './vocabulary.ts'

/**
 * The queue's filters: in the URL, and on the account.
 *
 * Two jobs that were being done by one `localStorage` key, badly.
 *
 * **The URL is what the view is.** A filtered queue is a thing people send
 * each other — "look at the three in aguardando correção" — and a screen whose
 * status lives only in memory cannot be sent, cannot be reloaded, and cannot be
 * left with the back button.
 *
 * **The account is what it starts as.** Filters were kept per browser, so the
 * same pharmacist on the counter machine and on their own laptop got two
 * different queues, and a cleared cache lost the choice. A person is one
 * person.
 *
 * So: the URL wins when it says anything, the saved preference fills in when
 * it does not, and every change writes to both.
 */
export interface QueueFilters {
  readonly values: Readonly<Record<string, string>>
  set(key: string, value: string): void
  /** False until the account's preferences have been read, so nothing saves over them. */
  readonly ready: boolean
}

/**
 * What each filter will accept.
 *
 * The address bar is user input like any other: it is typed, pasted, bookmarked
 * from an older version of this screen, and shared by somebody whose queue
 * looks nothing like yours. An unrecognised value is dropped rather than
 * passed along -- a stage of `banana` would reach SQL as a band that matches
 * nothing, and a queue that is empty for a reason nobody can see is the worst
 * answer this screen has.
 *
 * A filial that exists but is not yours needs no special handling: the query
 * is scoped to the account, so it simply matches nothing, and the option list
 * comes from the server rather than from the rows on screen.
 */
const ALLOWED: Readonly<Record<string, (value: string) => boolean>> = {
  /*
   * Asked of the vocabulary, not of a list copied beside it.
   *
   * Both of these were spelled out here as string literals, which gave the
   * address bar its own private opinion of what the statuses are -- one already
   * missing `concluding`, and one that would have gone on silently dropping
   * every status added after it, leaving somebody holding a link that filtered
   * to nothing for no visible reason.
   *
   * The label tables rather than `@rp/core`: this app is its own build and
   * does not import the server's packages, so they are already this side's
   * copy of the wire's vocabulary, and a third copy is what caused this.
   */
  stage: (value) => value in STAGE_LABEL,
  status: (value) => value in STATUS_LABEL,
  // Any integer: which ones exist is the server's business, and a code that
  // does not simply returns nothing.
  branch: (value) => /^\d+$/.test(value),
}

/** Keeps the recognisable parts of a comma-separated list, or nothing. */
function sanitise(key: string, raw: string): string {
  const allows = ALLOWED[key]
  if (allows === undefined) return raw
  return raw
    .split(',')
    .map((one) => one.trim())
    .filter((one) => one !== '' && allows(one))
    .join(',')
}

/** What the queue starts as for somebody who has never chosen. */
const DEFAULTS: Readonly<Record<string, string>> = {
  stage: 'comercial,conferencia',
  status: '',
  branch: '',
}

const KEYS = Object.keys(DEFAULTS)

/**
 * Whether the address is already asking for something.
 *
 * Separate from reading the values because the two answer different
 * questions: what the filters are, and whether the account may overwrite them.
 * A link somebody was sent is a deliberate view, not a stale one.
 */
function urlHasFilters(): boolean {
  const params = new URLSearchParams(window.location.search)
  return KEYS.some((key) => params.get(key) !== null)
}

function fromUrl(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)
  const found: Record<string, string> = {}
  for (const key of KEYS) {
    const value = params.get(key)
    if (value !== null) found[key] = sanitise(key, value)
  }
  return found
}

export function useQueueFilters(): QueueFilters {
  const [values, setValues] = useState<Record<string, string>>(() => ({
    ...DEFAULTS,
    ...fromUrl(),
  }))
  // The URL already said something, so the account must not overwrite it: a
  // link somebody was sent is a deliberate view, not a stale one.
  const [ready, setReady] = useState(urlHasFilters)

  useEffect(() => {
    if (ready) return
    let live = true
    void api
      .preferences()
      .then((answer) => {
        const saved = answer.preferences['queue']
        if (live && saved !== null && typeof saved === 'object') {
          // Sanitised on the way in as well: a preference saved months ago can
          // name a status that no longer exists, and it arrives with exactly
          // as much authority as a pasted URL.
          const clean: Record<string, string> = {}
          for (const [key, value] of Object.entries(saved as Record<string, unknown>)) {
            if (typeof value === 'string' && KEYS.includes(key)) clean[key] = sanitise(key, value)
          }
          setValues((current) => ({ ...current, ...clean }))
        }
      })
      .catch(() => undefined)
      .finally(() => {
        // Ready either way. A preference that cannot be read costs somebody
        // their defaults, never their screen.
        if (live) setReady(true)
      })
    return () => {
      live = false
    }
  }, [ready])

  /*
   * Written to the URL with `replaceState`.
   *
   * Ticking a filter is not navigation: pushing would make the back button
   * walk somebody through every checkbox they touched before returning them to
   * where they came from.
   */
  useEffect(() => {
    if (!ready) return
    const params = new URLSearchParams(window.location.search)
    for (const key of KEYS) {
      if (values[key] === undefined || values[key] === DEFAULTS[key]) params.delete(key)
      else params.set(key, values[key])
    }
    const query = params.toString()
    const next = `${window.location.pathname}${query === '' ? '' : `?${query}`}`
    if (next !== `${window.location.pathname}${window.location.search}`) {
      window.history.replaceState(null, '', next)
    }
  }, [values, ready])

  const set = useCallback((key: string, value: string) => {
    setValues((current) => {
      const next = { ...current, [key]: value }
      // Saved without waiting and without blocking: the screen already shows
      // what they chose, and a failed write costs them the next visit's
      // starting point rather than this one.
      void api.savePreference('queue', next).catch(() => undefined)
      return next
    })
  }, [])

  return { values, set, ready }
}
